import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ChevronLeft, ChevronRight, ShoppingBag } from 'lucide-react';
import { useShopifyOrders } from '../hooks/useShopifyOrders.js';
import { formatCurrency, formatDate } from '../lib/format.js';
import PeriodSelector, { resolveShortcut } from '../components/PeriodSelector.jsx';

const FINANCIAL_STATUS_OPTIONS = [
  { value: '', label: 'Tous les statuts' },
  { value: 'paid', label: 'Payée' },
  { value: 'pending', label: 'En attente' },
  { value: 'refunded', label: 'Remboursée' },
  { value: 'partially_refunded', label: 'Partiellement remboursée' },
  { value: 'voided', label: 'Annulée' },
];

const STATUS_BADGE = {
  paid:                { label: 'Payée',        className: 'bg-green-100 text-green-700' },
  pending:             { label: 'En attente',   className: 'bg-yellow-100 text-yellow-700' },
  refunded:            { label: 'Remboursée',   className: 'bg-slate-100 text-slate-600' },
  partially_refunded:  { label: 'Part. remb.',  className: 'bg-orange-100 text-orange-700' },
  voided:              { label: 'Annulée',      className: 'bg-red-100 text-red-700' },
};

function StatusBadge({ status }) {
  const conf = STATUS_BADGE[status] ?? { label: status ?? '—', className: 'bg-slate-100 text-slate-500' };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${conf.className}`}>
      {conf.label}
    </span>
  );
}

export default function ShopifyOrdersListPage() {
  const navigate = useNavigate();
  const [period, setPeriod] = useState(() => resolveShortcut('thisMonth'));
  const [financialStatus, setFinancialStatus] = useState('');
  const [searchMode, setSearchMode] = useState('q'); // 'q' | 'sku'
  const [searchInput, setSearchInput] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    const t = setTimeout(() => {
      setSearchDebounced(searchInput.trim().length >= 2 ? searchInput.trim() : '');
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  function handleFilterChange(setter) {
    return (value) => {
      setter(value);
      setPage(1);
    };
  }

  const filters = {
    from: period?.from,
    to: period?.to,
    financial_status: financialStatus || undefined,
    q: searchMode === 'q' ? searchDebounced || undefined : undefined,
    sku: searchMode === 'sku' ? searchDebounced || undefined : undefined,
    page,
    per_page: 50,
  };

  const { data, isLoading, error } = useShopifyOrders(filters);

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / 50));

  return (
    <div className="space-y-4">
      {/* Filtres */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-4 py-4 space-y-4">
        <PeriodSelector onChange={handleFilterChange(setPeriod)} label="Période" />

        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          {/* Toggle mode recherche */}
          <div className="flex rounded-lg border border-slate-200 overflow-hidden shrink-0">
            <button
              type="button"
              onClick={() => { setSearchMode('q'); setSearchInput(''); }}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                searchMode === 'q' ? 'bg-slate-800 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
              }`}
            >
              N° / Email
            </button>
            <button
              type="button"
              onClick={() => { setSearchMode('sku'); setSearchInput(''); }}
              className={`px-3 py-1.5 text-xs font-medium transition-colors border-l border-slate-200 ${
                searchMode === 'sku' ? 'bg-slate-800 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
              }`}
            >
              SKU
            </button>
          </div>

          {/* Search bar */}
          <div className="relative flex-1 min-w-0">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden />
            <input
              type="search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={searchMode === 'sku' ? 'Rechercher par SKU…' : 'N° commande, email…'}
              className="w-full pl-8 pr-3 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>

          {/* Filtre statut */}
          <select
            value={financialStatus}
            onChange={(e) => handleFilterChange(setFinancialStatus)(e.target.value)}
            className="text-sm border border-slate-300 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white"
            aria-label="Filtre statut financier"
          >
            {FINANCIAL_STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        {total > 0 && (
          <p className="text-xs text-slate-500">
            {total.toLocaleString('fr-FR')} commande{total > 1 ? 's' : ''}
          </p>
        )}
      </div>

      {/* Erreur */}
      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
          Impossible de charger les commandes : {error.message}
        </div>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-16 bg-slate-100 animate-pulse rounded-xl" />
          ))}
        </div>
      )}

      {/* Empty */}
      {!isLoading && !error && rows.length === 0 && (
        <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
          <ShoppingBag size={32} className="text-slate-300" />
          <p className="text-sm text-slate-500">Aucune commande sur cette période.</p>
          <p className="text-xs text-slate-400">Essaie une période plus large ou modifie les filtres.</p>
        </div>
      )}

      {/* Mobile cards */}
      {!isLoading && rows.length > 0 && (
        <>
          <div className="space-y-2 md:hidden">
            {rows.map((order) => (
              <button
                key={order.id}
                type="button"
                onClick={() => navigate(`/ventes/commandes/${order.id}`)}
                className="w-full text-left bg-white rounded-xl border border-slate-200 shadow-sm px-4 py-3 hover:bg-slate-50 active:bg-slate-100 transition-colors"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-900 truncate">
                      {order.name ?? `#${order.order_number}`}
                    </p>
                    <p className="text-xs text-slate-500 mt-0.5 truncate">
                      {order.customer_email ?? '—'}
                      {order.billing_city && ` · ${order.billing_city}`}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <span className="text-sm font-semibold text-slate-900 tabular-nums">
                      {formatCurrency((order.total_price_cents ?? 0))}
                    </span>
                    <StatusBadge status={order.financial_status} />
                  </div>
                </div>
                <p className="text-xs text-slate-400 mt-1">{formatDate(order.created_at_shopify)}</p>
              </button>
            ))}
          </div>

          {/* Desktop table */}
          <div className="hidden md:block bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50/50">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Date</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">N°</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Client</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Pays</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Total TTC</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Statut</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((order) => (
                  <tr
                    key={order.id}
                    onClick={() => navigate(`/ventes/commandes/${order.id}`)}
                    className="hover:bg-slate-50 cursor-pointer transition-colors"
                  >
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{formatDate(order.created_at_shopify)}</td>
                    <td className="px-4 py-3 font-medium text-slate-900 whitespace-nowrap">{order.name ?? `#${order.order_number}`}</td>
                    <td className="px-4 py-3 text-slate-600 max-w-[200px] truncate">{order.customer_email ?? '—'}</td>
                    <td className="px-4 py-3 text-slate-600">{order.billing_country_code ?? '—'}</td>
                    <td className="px-4 py-3 text-right font-semibold tabular-nums text-slate-900 whitespace-nowrap">
                      {formatCurrency(order.total_price_cents ?? 0)}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={order.financial_status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between gap-4">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronLeft size={14} aria-hidden /> Précédent
              </button>
              <span className="text-xs text-slate-500">
                Page {page} / {totalPages}
              </span>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Suivant <ChevronRight size={14} aria-hidden />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
